export type ProductGuideSelectionMode = 'SingleChoice' | 'MultipleChoice';
export type ProductGuideTemplateKey = 'build-package' | 'recommend-one' | 'find-compatible' | 'custom';
export type ProductGuideVehicleMode = 'Required' | 'Optional' | 'NotUsed';
export type ProductGuideDiscountType = 'Percentage' | 'FixedAmount';
export type ProductGuideItemType = 'FilteredProductChoice' | 'FixedProduct';

// Shopify collection option used anywhere the guide needs a reusable product set.
export interface ProductGuideCollectionOption {
    id: number;
    title: string;
    handle: string;
    collectionType: string;
}

// Compact row returned by the list endpoint.
export interface ProductGuideSummary {
    id: number;
    name: string;
    handle: string;
    description: string;
    guideType: string;
    templateKey: ProductGuideTemplateKey | string;
    vehicleMode: ProductGuideVehicleMode | string;
    isActive: boolean;
    requiresVehicleContext: boolean;
    bundleBuilderEnabled: boolean;
    discountEnabled: boolean;
    discountMinimumItems?: number | null;
    discountType: ProductGuideDiscountType | string;
    discountPercentage?: number | null;
    discountFixedAmount?: number | null;
    questionCount: number;
    resultGroupCount: number;
    recommendedPackageCount: number;
    createdAt: string;
    updatedAt?: string | null;
}

// Full editable guide shape used by create, update, and detail calls.
export interface ProductGuideDetail {
    id?: number | null;
    name: string;
    handle: string;
    description: string;
    guideType: string;
    templateKey: ProductGuideTemplateKey | string;
    vehicleMode: ProductGuideVehicleMode | string;
    isActive: boolean;
    requiresVehicleContext: boolean;
    bundleBuilderEnabled: boolean;
    discountEnabled: boolean;
    discountMinimumItems?: number | null;
    discountType: ProductGuideDiscountType | string;
    discountPercentage?: number | null;
    discountFixedAmount?: number | null;
    questions: ProductGuideQuestion[];
    resultGroups: ProductGuideResultGroup[];
    recommendedPackages: ProductGuideRecommendedPackage[];
}

// One shopper-facing wizard step.
export interface ProductGuideQuestion {
    id?: number | null;
    prompt: string;
    helpText: string;
    selectionMode: ProductGuideSelectionMode;
    connectedShopifyMetafieldAttributeId?: number | null;
    connectedShopifyMetafieldAttributeName: string;
    connectedShopifyMetafieldAttributeOwnerType: string;
    connectedShopifyMetafieldAttributeNamespace: string;
    connectedShopifyMetafieldAttributeKey: string;
    isRequired: boolean;
    displayOrder: number;
    answers: ProductGuideAnswer[];
}

// One selectable option under a question.
export interface ProductGuideAnswer {
    id?: number | null;
    label: string;
    valueKey: string;
    helpText: string;
    imageUrl: string;
    isEnabled: boolean;
    isDefault: boolean;
    displayOrder: number;
    matchingValues: ProductGuideAnswerMatchingValue[];
    rules: ProductGuideAnswerRule[];
}

export interface ProductGuideAnswerMatchingValue {
    value: string;
    normalizedValue: string;
}

// Filtering effect contributed when this answer is selected.
export interface ProductGuideAnswerRule {
    id?: number | null;
    source: 'ShopifyCollection' | 'ShopifyMetafieldAttribute';
    effect: 'Require' | 'Exclude' | 'Prefer';
    shopifyCollectionId?: number | null;
    shopifyCollectionTitle: string;
    shopifyCollectionHandle: string;
    shopifyMetafieldAttributeId?: number | null;
    shopifyMetafieldAttributeName: string;
    shopifyMetafieldAttributeOwnerType: string;
    shopifyMetafieldAttributeNamespace: string;
    shopifyMetafieldAttributeKey: string;
    metafieldValue: string;
    normalizedMetafieldValue: string;
    isActive: boolean;
    displayOrder: number;
}

// One package slot such as wheels, tires, or accessories.
export interface ProductGuideResultGroup {
    id?: number | null;
    title: string;
    slotKey: string;
    description: string;
    itemType: ProductGuideItemType | string;
    isRequired: boolean;
    minSelections: number;
    maxSelections: number;
    defaultQuantity: number;
    minQuantity: number;
    maxQuantity: number;
    noResultsMessage: string;
    optionalAddOnCopy: string;
    discountEligible: boolean;
    applyFilteringToFixedItem: boolean;
    baseShopifyCollectionId?: number | null;
    baseShopifyCollectionTitle: string;
    baseShopifyCollectionHandle: string;
    noResultsBehavior: 'ShowEmpty' | 'HideGroup' | 'UseFallbackCollection';
    fallbackShopifyCollectionId?: number | null;
    fallbackShopifyCollectionTitle: string;
    fallbackShopifyCollectionHandle: string;
    displayOrder: number;
    filterScopes: ProductGuideResultGroupFilterScope[];
    products: ProductGuideResultProduct[];
}

// Defines which selected answer collection filters are relevant to a package slot.
export interface ProductGuideResultGroupFilterScope {
    id?: number | null;
    scopeType: 'ShopifyCollection' | 'VehicleFitment' | 'ShopifyMetafieldAttribute';
    shopifyCollectionId?: number | null;
    shopifyCollectionTitle: string;
    shopifyCollectionHandle: string;
    shopifyMetafieldAttributeId?: number | null;
    shopifyMetafieldAttributeName: string;
    shopifyMetafieldAttributeOwnerType: string;
    shopifyMetafieldAttributeNamespace: string;
    shopifyMetafieldAttributeKey: string;
    isActive: boolean;
}

// One candidate product or variant inside a package slot.
export interface ProductGuideResultProduct {
    id?: number | null;
    shopifyProductId: number;
    shopifyProductVariantId?: number | null;
    productTitle: string;
    variantTitle: string;
    sku: string;
    featuredImage: string;
    quantity: number;
    isDefaultSelected: boolean;
    displayOrder: number;
}

export interface ProductGuideRecommendedPackage {
    id?: number | null;
    name: string;
    description: string;
    imageUrl: string;
    isEnabled: boolean;
    discountEligible: boolean;
    displayOrder: number;
    items: ProductGuideRecommendedPackageItem[];
}

export interface ProductGuideRecommendedPackageItem {
    id?: number | null;
    productGuideResultGroupId: number;
    productGuideResultGroupTitle: string;
    shopifyProductId: number;
    shopifyProductVariantId?: number | null;
    productTitle: string;
    variantTitle: string;
    sku: string;
    featuredImage: string;
    quantity: number;
    displayOrder: number;
}

// Request shape used by the admin preview/storefront resolver.
export interface ProductGuideResolveRequest {
    selectedAnswerIds: number[];
    fieldValues: Record<string, string>;
}

// Resolver output grouped by package slot.
export interface ProductGuideResolveResponse {
    guideId: number;
    groups: ProductGuideResolvedGroup[];
}

// One resolved package slot with products plus explainability notes.
export interface ProductGuideResolvedGroup {
    resultGroupId: number;
    title: string;
    slotKey: string;
    description: string;
    itemType: ProductGuideItemType | string;
    isRequired: boolean;
    minSelections: number;
    maxSelections: number;
    defaultQuantity: number;
    minQuantity: number;
    maxQuantity: number;
    optionalAddOnCopy: string;
    discountEligible: boolean;
    hidden: boolean;
    emptyMessage: string;
    appliedFilters: string[];
    products: ProductGuideResolvedProduct[];
}

// Product row returned after the cumulative collection/rule resolver runs.
export interface ProductGuideResolvedProduct {
    shopifyProductId: number;
    shopifyProductVariantId?: number | null;
    productTitle: string;
    variantTitle: string;
    handle: string;
    vendor: string;
    productType: string;
    featuredImage: string;
    sku: string;
    price?: number | null;
    compareAtPrice?: number | null;
    inventoryQuantity?: number | null;
    inventoryPolicy: string;
    isAvailable: boolean;
}

// Starter guide used only by the "new guide" editor route.
export const createDefaultProductGuide = (): ProductGuideDetail => ({
    name: 'Wheel and tire guide',
    handle: 'wheel-and-tire',
    description: '',
    guideType: 'wheel-tire-package',
    templateKey: 'build-package',
    vehicleMode: 'Required',
    isActive: false,
    requiresVehicleContext: true,
    bundleBuilderEnabled: true,
    discountEnabled: false,
    discountMinimumItems: null,
    discountType: 'Percentage',
    discountPercentage: null,
    discountFixedAmount: null,
    questions: [
        {
            prompt: 'What type of setup are you building?',
            helpText: '',
            selectionMode: 'SingleChoice',
            connectedShopifyMetafieldAttributeId: null,
            connectedShopifyMetafieldAttributeName: '',
            connectedShopifyMetafieldAttributeOwnerType: '',
            connectedShopifyMetafieldAttributeNamespace: '',
            connectedShopifyMetafieldAttributeKey: '',
            isRequired: true,
            displayOrder: 1,
            answers: [
                {
                    label: 'Daily driver',
                    valueKey: 'daily-driver',
                    helpText: '',
                    imageUrl: '',
                    isEnabled: true,
                    isDefault: false,
                    displayOrder: 1,
                    matchingValues: [],
                    rules: [],
                },
                {
                    label: 'Trail focused',
                    valueKey: 'trail-focused',
                    helpText: '',
                    imageUrl: '',
                    isEnabled: true,
                    isDefault: false,
                    displayOrder: 2,
                    matchingValues: [],
                    rules: [],
                },
            ],
        },
    ],
    resultGroups: [
        {
            title: 'Wheels',
            slotKey: 'wheels',
            description: '',
            itemType: 'FilteredProductChoice',
            isRequired: true,
            minSelections: 1,
            maxSelections: 1,
            defaultQuantity: 4,
            minQuantity: 4,
            maxQuantity: 5,
            noResultsMessage: '',
            optionalAddOnCopy: '',
            discountEligible: true,
            applyFilteringToFixedItem: false,
            baseShopifyCollectionId: null,
            baseShopifyCollectionTitle: '',
            baseShopifyCollectionHandle: '',
            noResultsBehavior: 'ShowEmpty',
            fallbackShopifyCollectionId: null,
            fallbackShopifyCollectionTitle: '',
            fallbackShopifyCollectionHandle: '',
            displayOrder: 1,
            filterScopes: [],
            products: [],
        },
        {
            title: 'Tires',
            slotKey: 'tires',
            description: '',
            itemType: 'FilteredProductChoice',
            isRequired: true,
            minSelections: 1,
            maxSelections: 1,
            defaultQuantity: 4,
            minQuantity: 4,
            maxQuantity: 5,
            noResultsMessage: '',
            optionalAddOnCopy: '',
            discountEligible: true,
            applyFilteringToFixedItem: false,
            baseShopifyCollectionId: null,
            baseShopifyCollectionTitle: '',
            baseShopifyCollectionHandle: '',
            noResultsBehavior: 'ShowEmpty',
            fallbackShopifyCollectionId: null,
            fallbackShopifyCollectionTitle: '',
            fallbackShopifyCollectionHandle: '',
            displayOrder: 2,
            filterScopes: [],
            products: [],
        },
    ],
    recommendedPackages: [],
});
